import { IssueCode, ValidationIssue } from "../domain/entities/ValidationIssue";
import { ValidationIssueMapper } from "./mappers/ValidationIssueMapper";

/** gRPC status codes used by the feature. */
export enum GrpcStatus {
  INVALID_ARGUMENT = 3,
  FAILED_PRECONDITION = 9,
}

export const ERROR_ISSUES_TRAILER = "error-issues-bin";

type GrpcErrorLike = { code?: unknown; metadata?: unknown };

/**
 * Blocking issues of a rejected call, read from the `error-issues-bin` trailer.
 *
 * grpc-web 1.0.7 rejects with `{ code, message, metadata }`, where `metadata` is a plain object
 * holding the trailers (or the headers of a trailers-only response) as strings. It does not decode
 * `-bin` keys, so the value is the base64 text of a serialized `ValidationIssueList`.
 */
export class GrpcIssues {
  public readonly issues: readonly ValidationIssue[];

  private constructor(public readonly code: number, issues: readonly ValidationIssue[]) {
    this.issues = Object.freeze([...issues]);
  }

  /**
   * `undefined` when the error is not a gRPC rejection or carries no readable trailer, so that
   * callers keep today's handling.
   */
  static read(error: unknown): GrpcIssues | undefined {
    if (!error || typeof error !== "object") {
      return undefined;
    }
    const { code, metadata } = error as GrpcErrorLike;
    if (typeof code !== "number" || !metadata || typeof metadata !== "object") {
      return undefined;
    }
    const encoded = (metadata as Record<string, unknown>)[ERROR_ISSUES_TRAILER];
    if (typeof encoded !== "string" || encoded.trim() === "") {
      return undefined;
    }
    try {
      return new GrpcIssues(code, ValidationIssueMapper.fromBinaryList(GrpcIssues.decode(encoded)));
    } catch {
      return undefined;
    }
  }

  isInvalidArgument(): boolean {
    return this.code === GrpcStatus.INVALID_ARGUMENT;
  }

  isFailedPrecondition(): boolean {
    return this.code === GrpcStatus.FAILED_PRECONDITION;
  }

  find(code: IssueCode): ValidationIssue | undefined {
    return this.issues.find(issue => issue.is(code));
  }

  /** gRPC binary metadata is standard base64, padding optional. */
  private static decode(encoded: string): Uint8Array {
    const base64 = encoded.trim();
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
      throw new Error("Invalid base64 trailer");
    }
    const unpadded = base64.replace(/=+$/, "");
    const binary = atob(unpadded + "=".repeat((4 - (unpadded.length % 4)) % 4));
    return Uint8Array.from(binary, char => char.charCodeAt(0));
  }
}
